import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircleIcon, UserIcon, PhoneIcon, MapPinIcon, SparklesIcon, ClockIcon, AlertCircleIcon, LockIcon, MessageCircleIcon, Globe2Icon } from 'lucide-react';
import { trackLead } from '../utils/metaPixel';
const locations = ['Miami, FL - 175 SW 7th St #2009, Miami, FL 33130', 'New York, NY - 116 Central Park South, New York, NY 10019', 'New York Lipo - 58 E 79th St 4R, New York, NY 10075', 'Houston, TX - 2702 Westheimer Rd, Houston, TX 77098', 'Las Vegas, NV - 2451 W Horizon Ridge Pkwy Suite 130, Henderson, NV 89052', 'Phoenix, AZ - 10505 N 69th St Suite 1100, Scottsdale, AZ 85253'];
const treatments = ['Lipo 360', 'BBL (Brazilian Butt Lift)', 'Tummy Tuck', 'Mommy Makeover', 'Face Tightening / Dermalift', 'Weight Loss (Ozempic/Semaglutide)', 'Laser Lipo', 'Skin Rejuvenation', 'Body Contouring', 'Breast Augmentation', 'Other'];
const languages = ['Español', 'English', 'Português'];
export function LeadForm() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    city: '',
    treatment: '',
    language: '',
    whatsappOptIn: true // Default checked
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  // Countdown timer state
  const [timeLeft, setTimeLeft] = useState({
    hours: 23,
    minutes: 59,
    seconds: 59
  });
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev.seconds > 0) {
          return {
            ...prev,
            seconds: prev.seconds - 1
          };
        } else if (prev.minutes > 0) {
          return {
            hours: prev.hours,
            minutes: prev.minutes - 1,
            seconds: 59
          };
        } else if (prev.hours > 0) {
          return {
            hours: prev.hours - 1,
            minutes: 59,
            seconds: 59
          };
        }
        return prev;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    try {
      // Send to Google Sheets
      await fetch('https://script.google.com/macros/s/AKfycbwYOUR_DEPLOYMENT_ID/exec', {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          city: formData.city,
          treatment: formData.treatment,
          language: formData.language,
          whatsappOptIn: formData.whatsappOptIn,
          timestamp: new Date().toISOString()
        })
      });
      // Track Meta Pixel Lead event
      trackLead({
        content_name: formData.treatment || 'General Inquiry',
        content_category: 'Lead Form',
        value: 0,
        currency: 'USD'
      });
      setSubmitStatus('success');
      setFormData({
        name: '',
        phone: '',
        city: '',
        treatment: '',
        language: '',
        whatsappOptIn: true
      });
      setTimeout(() => setSubmitStatus('idle'), 5000);
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus('idle'), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value,
      type
    } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  return <section id="lead-form" className="w-full py-20 lg:py-24 bg-gradient-to-br from-white via-brand-aqua/10 to-white relative overflow-hidden" data-id="element-235">
      {/* Premium decorative background */}
      <div className="absolute inset-0" data-id="element-236">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-brand-gold/10 rounded-full blur-3xl" data-id="element-237" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-brand-primary/10 rounded-full blur-3xl" data-id="element-238" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-239">
        {/* Premium Urgency banner - More elegant */}
        <motion.div initial={{
        opacity: 0,
        y: -20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="max-w-4xl mx-auto mb-12" data-id="element-240">
          <div className="bg-gradient-to-r from-red-500 via-red-600 to-red-500 rounded-2xl p-4 shadow-2xl border border-red-400/30" data-id="element-241">
            <div className="flex items-center justify-center gap-3 flex-wrap" data-id="element-242">
              <AlertCircleIcon className="w-6 h-6 text-white animate-pulse" data-id="element-243" />
              <p className="text-white font-bold text-base sm:text-lg" data-id="element-244">
                ⚡ Solo 7 cupos disponibles — Reserva ahora
              </p>
            </div>
          </div>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start" data-id="element-245">
          {/* Left content */}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-246">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand-gold/10 rounded-full mb-6" data-id="element-247">
              <SparklesIcon className="w-4 h-4 text-brand-gold" data-id="element-248" />
              <span className="text-brand-gold font-semibold text-sm uppercase tracking-wider" data-id="element-249">
                Consulta Gratuita
              </span>
            </div>

            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-brand-navy mb-6 leading-tight" data-id="element-250">
              Reserva Tu{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-primary to-brand-gold" data-id="element-251">
                Evaluación Gratuita
              </span>
            </h2>
            <p className="text-xl text-gray-600 mb-10 leading-relaxed" data-id="element-252">
              Espacios limitados por alta demanda. Da el primer paso hacia tu
              transformación.
            </p>

            {/* Premium Countdown Timer */}
            <div className="bg-gradient-to-br from-brand-navy via-brand-deep to-brand-navy rounded-3xl p-8 mb-10 shadow-2xl border border-brand-gold/20" data-id="element-253">
              <div className="flex items-center gap-3 mb-6" data-id="element-254">
                <ClockIcon className="w-6 h-6 text-brand-gold" data-id="element-255" />
                <p className="text-white font-bold text-lg" data-id="element-256">
                  Esta oferta expira en:
                </p>
              </div>
              <div className="grid grid-cols-3 gap-4" data-id="element-257">
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-5 text-center border border-brand-gold/20" data-id="element-258">
                  <div className="text-4xl font-bold text-brand-gold mb-2" data-id="element-259">
                    {String(timeLeft.hours).padStart(2, '0')}
                  </div>
                  <div className="text-white/80 text-sm font-medium" data-id="element-260">Horas</div>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-5 text-center border border-brand-gold/20" data-id="element-261">
                  <div className="text-4xl font-bold text-brand-gold mb-2" data-id="element-262">
                    {String(timeLeft.minutes).padStart(2, '0')}
                  </div>
                  <div className="text-white/80 text-sm font-medium" data-id="element-263">
                    Minutos
                  </div>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-5 text-center border border-brand-gold/20" data-id="element-264">
                  <div className="text-4xl font-bold text-brand-gold mb-2" data-id="element-265">
                    {String(timeLeft.seconds).padStart(2, '0')}
                  </div>
                  <div className="text-white/80 text-sm font-medium" data-id="element-266">
                    Segundos
                  </div>
                </div>
              </div>
            </div>

            {/* Premium Trust points */}
            <div className="space-y-4 mb-8" data-id="element-267">
              <div className="flex items-center gap-3 p-4 bg-white rounded-2xl shadow-lg border border-brand-gold/10" data-id="element-268">
                <CheckCircleIcon className="w-6 h-6 text-brand-gold" data-id="element-269" />
                <span className="text-gray-700 font-semibold" data-id="element-270">
                  Financiamiento Disponible
                </span>
              </div>
              <div className="flex items-center gap-3 p-4 bg-white rounded-2xl shadow-lg border border-brand-gold/10" data-id="element-271">
                <CheckCircleIcon className="w-6 h-6 text-brand-gold" data-id="element-272" />
                <span className="text-gray-700 font-semibold" data-id="element-273">
                  Procedimientos Seguros FDA-Approved
                </span>
              </div>
              <div className="flex items-center gap-3 p-4 bg-white rounded-2xl shadow-lg border border-brand-gold/10" data-id="element-274">
                <CheckCircleIcon className="w-6 h-6 text-brand-gold" data-id="element-275" />
                <span className="text-gray-700 font-semibold" data-id="element-276">
                  Citas el Mismo Día Disponibles
                </span>
              </div>
            </div>

            {/* Aspirational image */}
            <div className="hidden lg:block" data-id="element-277">
              <img src="https://images.unsplash.com/photo-1524504388940-b1c1722653e1?w=800&h=600&fit=crop" alt="Transform your body - Achieve your dream physique" className="rounded-3xl shadow-2xl ring-2 ring-brand-gold/20 object-cover w-full h-[400px]" data-id="element-278" />
            </div>
          </motion.div>

          {/* Premium Form */}
          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} data-id="element-279">
            <form onSubmit={handleSubmit} className="bg-white rounded-3xl shadow-2xl p-10 border-4 border-brand-primary/20 ring-4 ring-brand-gold/10" data-id="element-280">
              {/* Success/Error Messages */}
              {submitStatus === 'success' && <div className="mb-6 bg-green-50 border-2 border-green-300 rounded-2xl p-5" data-id="element-281">
                  <p className="text-green-700 font-bold text-center text-lg" data-id="element-282">
                    ✅ ¡Gracias! Tu solicitud ha sido enviada. Te contactaremos
                    pronto.
                  </p>
                </div>}

              {submitStatus === 'error' && <div className="mb-6 bg-red-50 border-2 border-red-300 rounded-2xl p-5" data-id="element-283">
                  <p className="text-red-700 font-bold text-center text-lg" data-id="element-284">
                    ❌ Hubo un error. Por favor intenta de nuevo.
                  </p>
                </div>}

              {/* Scarcity badge - More elegant */}
              <div className="bg-gradient-to-r from-red-50 to-orange-50 border-2 border-red-300 rounded-2xl p-3 mb-8" data-id="element-285">
                <p className="text-red-700 font-bold text-center text-sm" data-id="element-286">
                  🔥 Solo 7 espacios esta semana
                </p>
              </div>

              <div className="text-center mb-8" data-id="element-287">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-brand-primary to-brand-gold rounded-3xl mb-4 shadow-lg" data-id="element-288">
                  <SparklesIcon className="w-8 h-8 text-white" data-id="element-289" />
                </div>
                <h3 className="text-2xl font-bold text-brand-navy mb-2" data-id="element-290">
                  Reserva Tu Espacio
                </h3>
                <p className="text-gray-600" data-id="element-291">Respuesta en menos de 24 horas</p>
              </div>

              <div className="space-y-5" data-id="element-292">
                {/* Name field - Required */}
                <div data-id="element-293">
                  <label htmlFor="name" className="block text-sm font-bold text-gray-700 mb-2" data-id="element-294">
                    Nombre Completo <span className="text-red-500" data-id="element-295">*</span>
                  </label>
                  <div className="relative" data-id="element-296">
                    <UserIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-297" />
                    <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} placeholder="Tu nombre completo" className="w-full pl-12 pr-4 py-3.5 bg-gray-50 border-2 border-gray-200 rounded-2xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-gold focus:border-brand-gold transition-all duration-300" required disabled={isSubmitting} data-id="element-298" />
                  </div>
                </div>

                {/* Phone field - Required */}
                <div data-id="element-299">
                  <label htmlFor="phone" className="block text-sm font-bold text-gray-700 mb-2" data-id="element-300">
                    Número de Teléfono <span className="text-red-500" data-id="element-301">*</span>
                  </label>
                  <div className="relative" data-id="element-302">
                    <PhoneIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-303" />
                    <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} placeholder="(555) 123-4567" className="w-full pl-12 pr-4 py-3.5 bg-gray-50 border-2 border-gray-200 rounded-2xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-gold focus:border-brand-gold transition-all duration-300" required disabled={isSubmitting} data-id="element-304" />
                  </div>
                </div>

                {/* City dropdown - Required */}
                <div data-id="element-305">
                  <label htmlFor="city" className="block text-sm font-bold text-gray-700 mb-2" data-id="element-306">
                    Ubicación Preferida <span className="text-red-500" data-id="element-307">*</span>
                  </label>
                  <div className="relative" data-id="element-308">
                    <MapPinIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-309" />
                    <select id="city" name="city" value={formData.city} onChange={handleChange} className="w-full pl-12 pr-4 py-3.5 bg-gray-50 border-2 border-gray-200 rounded-2xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-brand-gold focus:border-brand-gold transition-all duration-300 appearance-none cursor-pointer" required disabled={isSubmitting} data-id="element-310">
                      <option value="" data-id="element-311">Selecciona una ubicación</option>
                      {locations.map(location => <option key={location} value={location} data-id="element-312">
                          {location}
                        </option>)}
                    </select>
                  </div>
                </div>

                {/* Treatment dropdown - Required */}
                <div data-id="element-313">
                  <label htmlFor="treatment" className="block text-sm font-bold text-gray-700 mb-2" data-id="element-314">
                    Tratamiento de Interés{' '}
                    <span className="text-red-500" data-id="element-315">*</span>
                  </label>
                  <div className="relative" data-id="element-316">
                    <SparklesIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-317" />
                    <select id="treatment" name="treatment" value={formData.treatment} onChange={handleChange} className="w-full pl-12 pr-4 py-3.5 bg-gray-50 border-2 border-gray-200 rounded-2xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-brand-gold focus:border-brand-gold transition-all duration-300 appearance-none cursor-pointer" required disabled={isSubmitting} data-id="element-318">
                      <option value="" data-id="element-319">Selecciona un tratamiento</option>
                      {treatments.map(treatment => <option key={treatment} value={treatment} data-id="element-320">
                          {treatment}
                        </option>)}
                    </select>
                  </div>
                </div>

                {/* Language dropdown - Required */}
                <div data-id="element-321">
                  <label htmlFor="language" className="block text-sm font-bold text-gray-700 mb-2" data-id="element-322">
                    Idioma de Preferencia{' '}
                    <span className="text-red-500" data-id="element-323">*</span>
                  </label>
                  <div className="relative" data-id="element-324">
                    <Globe2Icon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-325" />
                    <select id="language" name="language" value={formData.language} onChange={handleChange} className="w-full pl-12 pr-4 py-3.5 bg-gray-50 border-2 border-gray-200 rounded-2xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-brand-gold focus:border-brand-gold transition-all duration-300 appearance-none cursor-pointer" required disabled={isSubmitting} data-id="element-326">
                      <option value="" data-id="element-327">Selecciona tu idioma</option>
                      {languages.map(language => <option key={language} value={language} data-id="element-328">
                          {language}
                        </option>)}
                    </select>
                  </div>
                </div>

                {/* WhatsApp opt-in - Required checkbox */}
                <div className="bg-[#25D366]/10 rounded-2xl p-4 border-2 border-[#25D366]/30" data-id="element-329">
                  <label className="flex items-start gap-3 cursor-pointer" data-id="element-330">
                    <input type="checkbox" id="whatsappOptIn" name="whatsappOptIn" checked={formData.whatsappOptIn} onChange={handleChange} className="mt-1 w-5 h-5 text-[#25D366] border-gray-300 rounded focus:ring-[#25D366]" required disabled={isSubmitting} data-id="element-331" />
                    <div className="flex-1" data-id="element-332">
                      <div className="flex items-center gap-2 mb-1" data-id="element-333">
                        <MessageCircleIcon className="w-5 h-5 text-[#25D366]" data-id="element-334" />
                        <span className="text-sm font-bold text-gray-800" data-id="element-335">
                          Confirmar por WhatsApp{' '}
                          <span className="text-red-500" data-id="element-336">*</span>
                        </span>
                      </div>
                      <p className="text-xs text-gray-600" data-id="element-337">
                        Acepto recibir confirmación y recordatorios por WhatsApp
                      </p>
                    </div>
                  </label>
                </div>

                {/* Submit button - Elegant & Dynamic */}
                <motion.button type="submit" disabled={isSubmitting} whileHover={{
                scale: 1.02
              }} whileTap={{
                scale: 0.98
              }} className="w-full py-4 bg-gradient-to-r from-brand-primary via-brand-gold to-brand-primary text-white font-bold text-base rounded-2xl transition-all duration-300 shadow-2xl shadow-brand-gold/40 hover:shadow-brand-gold/60 disabled:opacity-50 disabled:cursor-not-allowed relative overflow-hidden group" data-id="element-338">
                  <span className="relative z-10 flex items-center justify-center gap-2" data-id="element-339">
                    {isSubmitting ? <>
                        <ClockIcon className="w-5 h-5 animate-spin" data-id="element-340" />
                        Enviando...
                      </> : <>
                        <SparklesIcon className="w-5 h-5" data-id="element-341" />
                        Reservar Consulta Gratis
                        <span className="text-lg" data-id="element-342">→</span>
                      </>}
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-brand-deep to-brand-primary transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" data-id="element-343" />
                </motion.button>

                {/* Urgency message below button */}
                <p className="text-center text-sm text-red-600 font-semibold animate-pulse" data-id="element-344">
                  ⏰ Últimos espacios disponibles hoy
                </p>
              </div>

              {/* Premium trust indicators */}
              <div className="mt-6 flex flex-wrap justify-center gap-4 text-xs" data-id="element-345">
                <span className="flex items-center gap-1.5 text-gray-600" data-id="element-346">
                  <CheckCircleIcon className="w-4 h-4 text-brand-gold" data-id="element-347" />
                  <span className="font-medium" data-id="element-348">Respuesta en 24h</span>
                </span>
                <span className="flex items-center gap-1.5 text-gray-600" data-id="element-349">
                  <LockIcon className="w-4 h-4 text-brand-gold" data-id="element-350" />
                  <span className="font-medium" data-id="element-351">100% Confidencial</span>
                </span>
                <span className="flex items-center gap-1.5 text-gray-600" data-id="element-352">
                  <CheckCircleIcon className="w-4 h-4 text-brand-gold" data-id="element-353" />
                  <span className="font-medium" data-id="element-354">Sin Compromiso</span>
                </span>
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </section>;
}